import { Link, useSearchParams } from "react-router-dom";
import type { EntityDetail } from "../../shared/api";
import { MIN_COMPARE } from "../../shared/compare";
import { metaFor } from "../../shared/seo";
import { api } from "../lib/api";
import { useMeta } from "../lib/seo";
import { useRouteData, compareKey } from "../lib/routeData";
import { useCompare } from "../lib/compareStore";
import { origin } from "../lib/origin";
import { Spinner, ErrorState } from "../components/States";
import { Stars } from "../components/Stars";
import { fmtRating, fmtUsd } from "../lib/format";

export function Compare() {
  const [params] = useSearchParams();
  const ids = (params.get("ids") ?? "").split(",").map((s) => s.trim()).filter(Boolean);
  const { clear } = useCompare();

  const { data, error, loading } = useRouteData<EntityDetail[]>(compareKey(ids), async () => {
    const res = await api.compare(ids);
    return res.entities;
  });

  useMeta(
    data ? metaFor({ route: "compare", base: origin(), entities: data, indexable: true }) : null,
  );

  if (error) return <ErrorState message={error} />;
  if (loading) return <Spinner />;
  if (!data || data.length < MIN_COMPARE) {
    return (
      <div className="empty-state pad-empty">
        <h1>Nothing to compare</h1>
        <p>Pick two to four tokens, or two to four companies, from a directory page.</p>
        <Link to="/tokens" className="btn btn-primary">Browse tokens</Link>
      </div>
    );
  }

  const dimensions = data[0]!.dimensionAverages.map((d) => ({ key: d.key, label: d.label }));
  const isToken = data[0]!.type === "token";

  return (
    <div>
      <div className="row between items-baseline">
        <h1>{data.map((e) => e.name).join(" vs ")}</h1>
        <button className="btn btn-ghost btn-sm" onClick={clear}>Clear selection</button>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">&nbsp;</th>
              {data.map((e) => (
                <th key={e.id} scope="col">
                  <Link to={`/${e.type}/${e.slug}`}>{e.name}</Link>
                  {e.symbol ? <span className="muted"> {e.symbol}</span> : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">Weighted rating</th>
              {data.map((e) => (
                <td key={e.id} className="num">
                  {fmtRating(e.ratingWeighted)} <Stars value={e.ratingWeighted} />
                </td>
              ))}
            </tr>
            <tr>
              <th scope="row">Raw average</th>
              {data.map((e) => <td key={e.id} className="num">{fmtRating(e.ratingRaw)}</td>)}
            </tr>
            <tr>
              <th scope="row">Reviews</th>
              {data.map((e) => <td key={e.id} className="num">{e.reviewCount}</td>)}
            </tr>
            <tr>
              <th scope="row">Verified / unverified</th>
              {data.map((e) => (
                <td key={e.id} className="num">
                  {e.ratingBreakdown.verified.count} / {e.ratingBreakdown.unverified.count}
                </td>
              ))}
            </tr>
            <tr>
              <th scope="row">Trust Score</th>
              {data.map((e) => (
                <td key={e.id} className="num">
                  {e.trustScore != null ? Math.round(e.trustScore) : "not enough data"}
                </td>
              ))}
            </tr>

            {dimensions.map((dim) => (
              <tr key={dim.key}>
                <th scope="row">{dim.label}</th>
                {data.map((e) => {
                  const found = e.dimensionAverages.find((d) => d.key === dim.key);
                  return (
                    <td key={e.id} className="num">
                      {found && found.count > 0 ? fmtRating(found.avg) : "—"}
                    </td>
                  );
                })}
              </tr>
            ))}

            {isToken ? (
              <>
                <tr>
                  <th scope="row">Chain</th>
                  {data.map((e) => <td key={e.id}>{e.signals.chain ?? "—"}</td>)}
                </tr>
                <tr>
                  <th scope="row">Market cap</th>
                  {data.map((e) => (
                    <td key={e.id} className="num">{fmtUsd(e.signals.market?.marketCapUsd)}</td>
                  ))}
                </tr>
              </>
            ) : (
              <>
                <tr>
                  <th scope="row">Founded</th>
                  {data.map((e) => <td key={e.id} className="num">{e.signals.foundedYear ?? "—"}</td>)}
                </tr>
                <tr>
                  <th scope="row">Headquarters</th>
                  {data.map((e) => <td key={e.id}>{e.signals.hqCountry ?? "—"}</td>)}
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      <p className="nfa u-49fcd85">
        <strong>Not financial advice.</strong> This comparison presents user opinions and verifiable
        facts, not a recommendation.
      </p>
    </div>
  );
}
